<template>
  <div>
    <h4 class="mb-4 font-weight-bold">Optional Filters</h4>
    <SaveReportWizardUnderwriterFilters
        v-if="'Underwriter' === userCompanyRole"
        v-model="underwriterModelValue"
    />
    <b-row>
      <b-col
          md="6"
      >
        <h5 class="mb-3 font-weight-bold">Select Parties</h5>
        <div class="mb-3 d-flex flex-wrap">
          <SaveReportWizardUnderwriter
              :label="underwriterLabel"
              v-model="modelValue.Underwriter_Name"
              @input="handleEmitValues"
              :loading="isFetchingParameters"
              :options="optionsList.Underwriter_Name"
              class="mr-2 mb-2"
          />
          <SaveReportWizardCoverholder
              v-model="modelValue.Coverholder_Name"
              @input="handleEmitValues"
              :loading="isFetchingParameters"
              :options="optionsList.Coverholder_Name"
              class="mr-2 mb-2"
          />
          <SaveReportWizardTPA
              v-model="modelValue.TPA_Name"
              @input="handleEmitValues"
              :loading="isFetchingParameters"
              :options="optionsList.TPA_Name"
              class="mr-2 mb-2"
          />
        </div>

        <h5 class="mb-3 font-weight-bold">Select Program</h5>
        <div class="mb-4 d-flex flex-wrap">
          <SaveReportWizardUMR
              v-model="modelValue.Unique_Market_Reference_UMR"
              @input="handleEmitValues"
              :loading="isFetchingParameters"
              :options="optionsList.Unique_Market_Reference_UMR"
              class="mr-2 mb-2"
          />
        </div>
      </b-col>
      <b-col md="6">
        <SaveReportWizardFlagStatus :disabled="isAllExports" v-model="flagModelValue" />
        <SaveReportWizardJurisdiction v-model="jurisdictionValue" :loading="isFetchingParameters" :options="optionsList.Jurisdiction" />
      </b-col>
      <b-col md="6">
        <h5 class="mb-3 font-weight-bold">Select Export Filter (Required)</h5>
        <div class="d-flex flex-wrap" data-toggle="buttons">
          <button
              @click="() => setDateType('end-date')"
              :class="{ active: isDateType('end-date') }"
              class="btn badge badge-pill mr-2 mb-2 text-capitalize"
          >
            By Report Date
          </button>

          <button
              @click="() => setDateType('submission-date')"
              :class="{ active: isDateType('submission-date') }"
              class="btn badge badge-pill mr-2 mb-2 text-capitalize"
          >
            By Submission Date
          </button>
          <button
              v-if="combinedExportAvailable"
              @click="() => setDateType('all-exports')"
              :class="{ active: isDateType('all-exports') }"
              class="btn badge badge-pill mr-2 mb-2 text-capitalize"
          >
            Combined Export
          </button>
        </div>
      </b-col>
      <b-col md="6">
        <SaveReportWizardBordeauxStatusFilters
            v-model="bordeauxStatusValue"
            class="mb-4"
            :disabled=isAllExports
        />
      </b-col>
    </b-row>

    <SaveReportWizardEndDate
        v-if="'end-date' == dateType"
        v-model="modelValue.Reporting_Period_End_Date"
        @input="handleEmitValues"
        :loading="isFetchingParameters"
        :options="optionsList.Reporting_Period_End_Date"
        class="mb-4"
    />
    <SaveReportWizardSubmissionDate
        v-if="'submission-date' == dateType"
        v-model="modelValue.modified_on"
        @input="handleEmitValues"
    />
  </div>
</template>

<script>
import axios from '@util/axios'
import moment from 'moment'
import { debounce } from 'lodash-es'

import { DATE_FORMAT } from '@constants'
import { userMixin } from '@mixins'

import { splitByOptions } from './utils'

import SaveReportWizardTPA from './components/SaveReportWizardTPA.vue'
import SaveReportWizardUnderwriter from './components/SaveReportWizardUnderwriter.vue'
import SaveReportWizardCoverholder from './components/SaveReportWizardCoverholder.vue'
import SaveReportWizardUMR from './components/SaveReportWizardUMR.vue'
import SaveReportWizardFlagStatus from './components/SaveReportWizardFlagStatus.vue'
import SaveReportWizardEndDate from './components/SaveReportWizardEndDate.vue'
import SaveReportWizardSubmissionDate from './components/SaveReportWizardSubmissionDate.vue'
import SaveReportWizardBordeauxStatusFilters from './components/SaveReportWizardBordeauxStatusFilters.vue'
import SaveReportWizardUnderwriterFilters from './components/SaveReportWizardUnderwriterFilters.vue'
import reportsService from '../../services/reportsService'
import SaveReportWizardJurisdiction from "@components/SaveReportWizard/components/SaveReportWizardJurisdiction.vue";

export default {
  props: {
    value: {
      type: [Array, Object],
      default: () => []
    },
    flagStatusSet: {
      type: Array,
      default: () => []
    },
    bordeauxStatus: String,
    splitBy: [String, Array],
    underwriter: {
      type: String,
      default: 'Lead'
    },
    dateType: {
      type: String,
      default: null
    },
    jurisdiction: {
      type: Array,
      default: () => []
    }
  },
  components: {
    SaveReportWizardJurisdiction,
    SaveReportWizardTPA,
    SaveReportWizardUnderwriter,
    SaveReportWizardCoverholder,
    SaveReportWizardUMR,
    SaveReportWizardFlagStatus,
    SaveReportWizardEndDate,
    SaveReportWizardSubmissionDate,
    SaveReportWizardBordeauxStatusFilters,
    SaveReportWizardUnderwriterFilters
  },
  data() {
    return {
      isFetchingParameters: true,
      optionsList: {
        TPA_Name: [],
        Underwriter_Name: [],
        Coverholder_Name: [],
        Unique_Market_Reference_UMR: [],
        Reporting_Period_End_Date: [],
        Jurisdiction: [],
      },
      updatingJurisdiction: false,
    }
  },
  mixins: [userMixin],
  computed: {
    modelValue: {
      get() {
        if (!Array.isArray(this.value)) {
          const objectEntries = Object.entries(this.value).map((item) => {
            if (Array.isArray(item[1])) {
              item[1] = item[1].filter((value) => {
                // if (this.optionsList[item[0]] === 'Jurisdiction') return true;
                return this.optionsList[item[0]].includes(value)
              })
            }

            return item
          })

          return Object.fromEntries(objectEntries)
        }

        if (!this.value.length)
          return {
            TPA_Name: [],
            Underwriter_Name: [],
            Coverholder_Name: [],
            Unique_Market_Reference_UMR: [],
            Reporting_Period_End_Date: null,
            modified_on: null,
            Jurisdiction: []
          }

        const valueEntries = this.value.map((item) => {
          if (item.type && 'Range' === item.type) {
            const rangeValue = item.value.split(',')

            return [
              item.name,
              {
                from: rangeValue[0],
                to: rangeValue[1]
              }
            ]
          } else {
            if (!item.value || !item.value.length) {
              return [item.name, item.value]
            } else {
              const filteredValues = item.value.filter((value) => {
                if ('Reporting_Period_End_Date' === item.name) {
                  value = moment(value, 'MM-DD-YYYY').format(DATE_FORMAT)
                }

                return this.optionsList[item.name].includes(value)
              })

              return [item.name, filteredValues]
            }
          }
        })
        const objectEntries = Object.fromEntries(valueEntries)

        if (
            !objectEntries.Reporting_Period_End_Date &&
            !objectEntries.modified_on
        ) {
          if ('submission-date' == this.dateType) {
            objectEntries.Reporting_Period_End_Date = null
          } else if ('end-date' == this.dateType) {
            objectEntries.modified_on = null
          } else {
            objectEntries.Reporting_Period_End_Date = null
            objectEntries.modified_on = null
          }
        }

        if (objectEntries.Reporting_Period_End_Date)
          this.$emit('set-date-type', 'end-date')
        else if (objectEntries.modified_on)
          this.$emit('set-date-type', 'submission-date')

        return objectEntries
      },
      set(newValue) {}
    },
    combinedExportAvailable() {
      return this.userCompanyRole === 'Underwriter' ||
          this.userCompanyRole === 'Broker' ||
          this.userCompanyRole === 'admin'
    },
    isAllExports() {
      return this.dateType === 'all-exports'
    },
    flagModelValue: {
      get() {
        return this.flagStatusSet
      },
      set(newValue) {
        this.$emit('set-flags', newValue)
      }
    },
    jurisdictionValue: {
      get() {
        return this.jurisdiction
      },
      set(newValue) {
        this.$emit('set-jurisdiction', newValue)
      }
    },
    bordeauxStatusValue: {
      get() {
        return this.bordeauxStatus
      },
      set(newValue) {
        this.$emit('set-bordeaux-status', newValue)
      }
    },
    underwriterModelValue: {
      get() {
        return this.underwriter
      },
      set(newValue) {
        this.$emit('set-underwriter', newValue)
      }
    },
    underwriterLabel: function () {
      return this.userCompanyRole === 'TPA' ||
      this.userCompanyRole === 'Underwriter'
          ? 'Select Lead Underwriters'
          : 'Select Underwriters'
    }
  },
  methods: {
    async getParameters(params = null, flags = null, umrCriteria = 'Active', jurisdiction = null) {
      this.isFetchingParameters = true

      const data = {
        company_id: this.userCompanyId,
        role: this.userCompanyRole,
        role_value: this.userCompanyRoleValue,
        umr_criteria: umrCriteria,
        uw_option: this.underwriterModelValue,
        all_exports: this.isAllExports
      }

      if (params) data.parameters = params

      if (flags) data.flag = flags

      if (jurisdiction) data.jurisdiction = jurisdiction

      reportsService.getExportParametersV2(data)
          .then((response) => {
            const parameters = response.data.data.data

            Object.keys(parameters).forEach((key) => {
              const newValues = parameters[key].filter(v => v);
              if (key === "Jurisdiction") {
                if (!this.optionsList.Jurisdiction.length && newValues.length) {
                  this.optionsList.Jurisdiction = newValues;
                }
                if (!this.updatingJurisdiction) {
                  this.updatingJurisdiction = true
                  this.jurisdictionValue = newValues.filter(j => this.optionsList.Jurisdiction.includes(j))
                  this.$nextTick(() => {
                    this.updatingJurisdiction = false
                  })
                }
                return;
              }
              this.optionsList[key] = newValues;
            });

            this.isFetchingParameters = false
          })
          .catch((error) => {
            this.$toasted.show(
                error.response.data.data.error || error.response.data.data.message,
                {
                  type: 'error'
                }
            )
          })
    },
    handleEmitValues() {
      const emitValues = Object.keys(this.modelValue)
          .filter((key) => {
            if (!this.modelValue[key]) return false
            else if (
                Array.isArray(this.modelValue[key]) &&
                !this.modelValue[key].length
            )
              return false
            else return true
          })
          .map((key) => {
            if (
                ['Reporting_Period_End_Date', 'modified_on'].includes(key) &&
                !Array.isArray(this.modelValue[key])
            ) {
              return {
                name: key,
                type: 'Range',
                value: `${this.modelValue[key].from},${this.modelValue[key].to}`
              }
            } else {
              return {
                name: key,
                value: this.modelValue[key]
              }
            }
          })

      this.$emit('input', emitValues)
    },
    isDateType(dateType) {
      return dateType === this.dateType
    },
    setDateType(newDateType) {
      this.$emit(
          'set-date-type',
          newDateType === this.dateType ? null : newDateType
      )
    }
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(newValue, oldValue) {
        if (!newValue.length && oldValue && oldValue.length)
          this.getParameters(null, this.flagStatusSet, this.bordeauxStatusValue, this.jurisdictionValue)
        else if (JSON.stringify(newValue) != JSON.stringify(oldValue))
          this.getParameters(
              newValue.length ? newValue : null,
              this.flagStatusSet,
              this.bordeauxStatusValue,
              this.jurisdictionValue
          )
      }
    },
    dateType(newType, oldType) {
      if (newType === 'all-exports') {
        if (this.modelValue.Reporting_Period_End_Date || this.modelValue.modified_on) {
          this.modelValue.Reporting_Period_End_Date = null;
          this.modelValue.modified_on = null;
        }

        if (this.bordeauxStatusValue !== 'All') this.bordeauxStatusValue = 'All';
        if (this.flagModelValue.length !== 0) this.flagModelValue = [];
        // Emit updated parameters
        this.handleEmitValues();
      } else if (oldType === 'all-exports') {
        this.$emit('reset-combined-export-warning')
        // Switching back from 'all-exports' to specific date type
        // Optionally, you can set default values or just emit current values
        this.bordeauxStatusValue = 'Active';
      }

    },
    jurisdictionValue: {
      handler(new_value) {
        if (this.updatingJurisdiction)  return;
        this.getParameters(
            this.value,
            this.flagStatusSet,
            this.bordeauxStatusValue,
            new_value
        );
      }
    },
    bordeauxStatusValue(newBordeauxStatusValue) {
      this.optionsList.Jurisdiction = []
      this.getParameters(this.value, this.flagStatusSet, newBordeauxStatusValue, [])
    },
    underwriterModelValue() {
      this.getParameters(
          this.value,
          this.flagStatusSet,
          this.bordeauxStatusValue,
          this.jurisdictionValue
      )
    }
  },
  mounted() {
    this.$on(
        'set-flags',
        debounce((payload) => {
          this.getParameters(
              this.value.length ? this.value : null,
              payload,
              this.bordeauxStatusValue,
              this.jurisdictionValue
          )
        }, 500)
    )
  }
}
</script>

<style scoped>
.form-group >>> .col-form-label {
  font-size: 13px !important;
}
</style>

<style lang="scss">
.modal-toggle {
  .el {
    &-loading {
      &-mask {
        background-color: rgba(255, 255, 255, 0.5) !important;
      }

      &-spinner {
        width: auto;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        transform: translateY(-50%);

        .circular {
          width: 20px;
          height: 20px;
        }

        .path {
          stroke: #000 !important;
          stroke-width: 3px;
        }
      }
    }
  }
}
</style>
